import { ComponentLabelSource, ReferenceLabelSource } from '@ardoq/data-model';
import { TabProps } from '@ardoq/tabs';
import { FormattingOption, PerspectiveTab } from './types';
import { HexColor, Palette } from '@ardoq/api-types';
import { colors } from '@ardoq/design-tokens';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

export const getDefaultRefLabelFormatting = (
  i18nLocale: I18nLocale
): FormattingOption => ({
  label: t(
    i18nLocale,
    'perspectives:labels.referenceLabelOptions.displayTextOrReferenceType',
    'Display text or Reference type (default)'
  ),
  value: ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE,
  description: t(
    i18nLocale,
    'perspectives:labels.referenceLabelOptions.displayTextOrReferenceTypeDescription',
    'The display text will be shown on references. If the field is blank, the reference type name will be shown.'
  ),
});

export const getDefaultRefLabelFormattingOpts = (
  i18nLocale: I18nLocale
): FormattingOption[] => [
  getDefaultRefLabelFormatting(i18nLocale),
  {
    label: t(
      i18nLocale,
      'perspectives:labels.referenceLabelOptions.displayText',
      'Display text'
    ),
    value: ReferenceLabelSource.DISPLAY_TEXT,
  },
  {
    label: t(
      i18nLocale,
      'perspectives:labels.referenceLabelOptions.referenceType',
      'Reference type'
    ),
    value: ReferenceLabelSource.REFERENCE_TYPE,
  },
];

export const getDefaultCompLabelFormatting = (
  i18nLocale: I18nLocale
): FormattingOption => ({
  label: t(
    i18nLocale,
    'perspectives:labels.componentLabelOptions.name',
    'Name (default)'
  ),
  value: ComponentLabelSource.NAME,
});

export const getDefaultCompLabelFormattingOpts = (
  i18nLocale: I18nLocale
): FormattingOption[] => [
  getDefaultCompLabelFormatting(i18nLocale),
  {
    label: t(
      i18nLocale,
      'perspectives:labels.componentLabelOptions.componentType',
      'Component type'
    ),
    value: ComponentLabelSource.TYPE,
  },
];

export const getTabsConfig = (
  i18nLocale: I18nLocale
): Record<PerspectiveTab, TabProps> => ({
  [PerspectiveTab.FILTERS]: {
    tabId: PerspectiveTab.FILTERS,
    label: t(i18nLocale, 'perspectives:tabs.filters', 'Filters'),
  },
  [PerspectiveTab.FORMATTING]: {
    tabId: PerspectiveTab.FORMATTING,
    label: t(i18nLocale, 'perspectives:tabs.format', 'Formatting'),
  },
  [PerspectiveTab.GROUPS]: {
    tabId: PerspectiveTab.GROUPS,
    label: t(i18nLocale, 'perspectives:tabs.group', 'Groups'),
  },
});

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
type TabsConfigEntriesT = Entries<Record<PerspectiveTab, TabProps>>;

export const getTabConfigEntries = (
  i18nLocale: I18nLocale
): TabsConfigEntriesT =>
  Object.entries(getTabsConfig(i18nLocale)) as TabsConfigEntriesT;

export const CUSTOM_FIELDS_FORMATTING_GROUP_LABEL = 'Custom fields';
export const DEFAULT_FORMATTING_GROUP_LABEL = 'Default';

const FORMATTING_COLOR_PICKER_PALETTE_COLORS: HexColor[][] = [
  [
    colors.orange200,
    colors.orange400,
    colors.orange600,
    colors.orange800,
    colors.orange950,
  ],
  [
    colors.green200,
    colors.green400,
    colors.green600,
    colors.green800,
    colors.green950,
  ],
  [
    colors.yellow200,
    colors.yellow300,
    colors.yellow500,
    colors.yellow700,
    colors.yellow950,
  ],

  [
    colors.blue200,
    colors.blue400,
    colors.blue600,
    colors.blue800,
    colors.blue950,
  ],
  [
    colors.brand200,
    colors.brand400,
    colors.brand600,
    colors.brand800,
    colors.brand950,
  ],
  [
    colors.pink200,
    colors.pink400,
    colors.pink600,
    colors.pink800,
    colors.pink950,
  ],
  [
    colors.grey200,
    colors.grey400,
    colors.grey500,
    colors.grey700,
    colors.grey950,
  ],
];
const paleOranges = ['#FCECE8', '#F5C4B7', '#ECB3A4', '#D3ACA1', '#B3A09B'];
const paleGreens = ['#E8FAF2', '#C4F2DD', '#AADDC5', '#A6C3B5', '#9EACA5'];
const paleYellows = ['#FCF6D2', '#FBEEB3', '#F7D8A0', '#DCB99D', '#C5AF9D'];
const paleBlues = ['#E8F0FD', '#C6DAFB', '#AEBFF3', '#A9B2DC', '#A4A7B9'];
const palePurples = ['#F1EBFE', '#DCC8FC', '#C99BFA', '#B39ACF', '#A49AAF'];
const palePinks = ['#FCECFC', '#F4C2F0', '#E4AADD', '#CCA4C5', '#B49BB0'];
const paleGrays = ['#F5F5F6', '#D9DADC', '#C6C7CA', '#B3B4B7', '#9A9B9B'];

// Pale versions of list field formatting colors
export const PALE_LIST_FIELD_COLORS = {
  green: '#73C9A1', // paleGreens[3] - corresponds to colors.green600
  softGreen: '#A2DBC1', // paleGreens[2] - corresponds to colors.green400
  orange: '#FAE19B', // paleYellows[2] - corresponds to colors.yellow300
  amber: '#F8D269', // paleYellows[3] - corresponds to colors.yellow500
  red: '#EC9669', // paleOranges[3] - corresponds to colors.orange600
};

const PALE_FORMATTING_COLOR_PICKER_PALETTE_COLORS: HexColor[][] = [
  paleOranges,
  paleGreens,
  paleYellows,
  paleBlues,
  palePurples,
  palePinks,
  paleGrays,
];

/**
 * Flattens a 2D palette of colors by interleaving column-wise.
 * Example: [arr[0][0], arr[1][0], arr[2][0], ... arr[0][1], arr[1][1], ...]
 */
const flattenPaletteColors = (palette: HexColor[][]): HexColor[] => {
  const maxLength = Math.max(...palette.map(row => row.length));
  return Array.from({ length: maxLength }).flatMap((_, colIdx) =>
    palette.map(row => row[colIdx]).filter(isTruthy)
  );
};

export const FORMATTING_DEFAULT_PALETTE: Palette = {
  label: 'Default',
  paletteColors: flattenPaletteColors(FORMATTING_COLOR_PICKER_PALETTE_COLORS),
};

export const FORMATTING_PALE_PALETTE: Palette = {
  label: 'Default',
  paletteColors: flattenPaletteColors(
    PALE_FORMATTING_COLOR_PICKER_PALETTE_COLORS
  ),
};

/**
 * Flattens a 2D palette using the custom traversal algorithm:
 * Start at middle column (column 4), middle row (row 2 from bottom)
 * Go left first (columns 3, 2, 1, 0), then right (columns 5, 6)
 * Then alternate: row above, row below, row above, row below
 */
const flattenAutomaticPaletteColors = (palette: HexColor[][]): HexColor[] => {
  const numCols = palette.length;
  const numRows = palette[0]?.length || 0;

  const startCol = 4; // 5th column (0-indexed)
  const startRow = 2; // 3rd row from bottom (0-indexed, so this is middle row)

  const result: HexColor[] = [];

  // Row sequence:
  // Pass 1: row 3 (index 2) - middle, starting point
  // Pass 2: row 2 (index 1) - above middle
  // Pass 3: row 4 (index 3) - below middle
  // Pass 4: row 1 (index 0) - above above
  // Pass 5: row 5 (index 4) - below below

  const rowSequence: number[] = [startRow]; // Start with middle row (index 2)

  // Alternate up and down from the starting row
  let upOffset = 1;
  let downOffset = 1;

  for (let i = 1; i < numRows; i++) {
    if (i % 2 === 1) {
      // Odd passes go up
      const rowIdx = startRow - upOffset;
      if (rowIdx >= 0) {
        rowSequence.push(rowIdx);
      }
      upOffset++;
    } else {
      // Even passes go down
      const rowIdx = startRow + downOffset;
      if (rowIdx < numRows) {
        rowSequence.push(rowIdx);
      }
      downOffset++;
    }
  }

  // For each row in the sequence
  for (const rowIdx of rowSequence) {
    // Column sequence: start at startCol, go left, then right
    const colSequence: number[] = [];

    // Add starting column
    colSequence.push(startCol);

    // Go left from startCol
    for (let col = startCol - 1; col >= 0; col--) {
      colSequence.push(col);
    }

    // Go right from startCol
    for (let col = startCol + 1; col < numCols; col++) {
      colSequence.push(col);
    }

    // Add colors from this row
    for (const colIdx of colSequence) {
      if (palette[colIdx] && palette[colIdx][rowIdx]) {
        result.push(palette[colIdx][rowIdx]);
      }
    }
  }

  return result;
};

export const AUTOMATIC_COLOR_ASSIGNMENT_COLORS: HexColor[] =
  flattenAutomaticPaletteColors(FORMATTING_COLOR_PICKER_PALETTE_COLORS);

export const PALE_AUTOMATIC_COLOR_ASSIGNMENT_COLORS: HexColor[] =
  flattenAutomaticPaletteColors(PALE_FORMATTING_COLOR_PICKER_PALETTE_COLORS);

export const FORMATTING_RULE_LINE_HEIGHT = 34;

// Rearranged order of color columns for color range formatting (start from brand color)
const COLOR_RANGE_COLOR_COLUMNS_ORDER = [4, 3, 2, 1, 0, 5, 6];
export const COLORS_FOR_COLOR_RANGE: HexColor[][] =
  COLOR_RANGE_COLOR_COLUMNS_ORDER.map(
    colIndex => FORMATTING_COLOR_PICKER_PALETTE_COLORS[colIndex]
  );

export const PALE_COLORS_FOR_COLOR_RANGE: HexColor[][] =
  COLOR_RANGE_COLOR_COLUMNS_ORDER.map(
    colIndex => PALE_FORMATTING_COLOR_PICKER_PALETTE_COLORS[colIndex]
  );

export const getUnsupportedConditionRuleMessage = (
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    'perspectives:formattingRules.unsupportedRuleMessage',
    'Set precise and clear rules with the new conditional format.\n"No value" (formerly "is empty") now colors only relevant data. Existing rules are applied but not editable.'
  );

export const getUnsupportedConditionRuleLinkText = (
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    'perspectives:formattingRules.unsupportedRuleLinkText',
    'Learn more here'
  );
